import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import { Box, Input as MuiInput, Tabs, Tab, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import { colors } from "../../theme/colors";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  useGetHealthOrganisationsQuery,
  useGetLaboratoriesQuery,
  useGetUserRolesQuery,
  useGetUsersListQuery,
  useUpdateUserMutation,
} from "../../services/api";
import { CustomTable, StatusBadge } from "../../components/DataTable";
import { CustomActionMenu } from "../../components/Menu";
import {
  ConfirmationDialog,
  DetailsDialog,
  InputDialog,
} from "../../components/Dialog";
import { Button, LabelBox } from "../../components";
import { Input } from "../../components/UserInput";
import { updateStatusSchema } from "../../yup";
import { getErrorMessage } from "../../utils/common";
import style from "./user.module.css";
import { buildUserActions, renderCellText, useStatusDialogs } from "./userShared";
import { DEFAULT_SUPER_ADMIN_ROLE_ID } from "../../utils/constants";

/** Form values inferred from the update status schema. */
type FormState = typeof updateStatusSchema.__outputType;
/** Action identifiers for the global admin action menu. */
type GlobalAdminAction = "view" | "edit" | "logs" | "deactivate" | "activate";

/** Tab identifiers for the Users page. */
type UsersTabKey = "healthOrg" | "laboratory" | "global";

/** Address shape used for rendering user list address/phone fields. */
type Address = HealthOrganisation["address"] | Laboratory["address"];

/**
 * Formats a human-readable address for list rendering.
 *
 * Args:
 *   address: Address object from the API.
 *
 * Returns:
 *   JSX.Element: Rendered address text.
 */
const renderAddressCell = (address?: Address) =>
  renderCellText(
    `${address?.unit_number ?? ""} ${address?.street_number ?? ""} ${address?.street_name ?? ""
    }, ${address?.suburb ?? ""} ${address?.state ?? ""} ${address?.postcode ?? ""
    }`,
    { className: style.cell }
  );

/**
 * Formats a phone number cell for list rendering.
 *
 * Args:
 *   address: Address object containing phone details.
 *
 * Returns:
 *   JSX.Element: Rendered phone text.
 */
const renderPhoneCell = (address?: Address) =>
  renderCellText(`${address?.phone ?? ""}`, { className: style.cell });

/**
 * Renders a status badge for list rows.
 *
 * Args:
 *   isActive: Status flag from the API.
 *
 * Returns:
 *   JSX.Element: Status badge component.
 */
const renderStatusBadge = (isActive?: boolean) => (
  <StatusBadge isActive={isActive ?? false} />
);

/**
 * Formats a global admin name for list rendering.
 *
 * Args:
 *   item: User row data from the API.
 *
 * Returns:
 *   JSX.Element: Rendered name text.
 */
const renderGlobalNameCell = (item: Users) =>
  renderCellText(
    `${item?.surname?.toUpperCase() ?? ""}, ${item?.given_name ?? ""} ${item?.prefix ?? ""
    }`,
    { className: style.cell }
  );

/**
 * Renders the Health Organisation/Laboratory list with search and tabs.
 *
 * Returns:
 *   JSX.Element: Users list page.
 */
const Users = () => {
  const navigate = useNavigate();
  // all query parameters and pagination params
  const [queryParam, setQueryParam] = useState({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: true,
    search_key: "",
  });
  const [selectedTab, setSelectedTab] = useState<UsersTabKey>("healthOrg");

  // Fetch only the active tab's dataset to avoid redundant list requests.
  const { data: healthOrgData, isFetching: healthOrgFetching } =
    useGetHealthOrganisationsQuery(queryParam, {
      skip: selectedTab !== "healthOrg",
    });
  const { data: laboratoryData, isFetching: laboratoryFetching } =
    useGetLaboratoriesQuery(queryParam, {
      skip: selectedTab !== "laboratory",
    });

  const { data: rolesData } = useGetUserRolesQuery({
    is_active: true,
    is_detailed: true,
  });

  // Resolve Super Admin role id from API so global list targets the correct role.
  const superAdminRoleId = useMemo(
    () =>
      rolesData?.items?.find(
        (role) => role.name === "Super Admin"
      )?.id,
    [rolesData]
  );

  const [userListQueryParam, setUserListQueryParam] = useState({
    search_key: "",
    order_by_columns: "surname",
    order: "Asc",
    is_detailed: true,
    user_role_id: superAdminRoleId ?? DEFAULT_SUPER_ADMIN_ROLE_ID,
  });

  const {
    data: superAdminData,
    isFetching: superAdminFetching,
    refetch: refetchGlobalAdmins,
  } = useGetUsersListQuery(userListQueryParam, {
    skip: selectedTab !== "global",
  });

  const [updateUser] = useUpdateUserMutation();
  const [selectedGlobalAdmin, setSelectedGlobalAdmin] = useState<Users | null>(null);
  const [globalMessage, setGlobalMessage] = useState<LabelMessageType | null>(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const {
    confirmOpen: globalConfirmOpen,
    reasonOpen: globalReasonOpen,
    logsOpen: globalLogsOpen,
    openConfirm: openGlobalConfirm,
    closeConfirm: closeGlobalConfirm,
    openReason: openGlobalReason,
    closeReason: closeGlobalReason,
    openLogs: openGlobalLogs,
    closeLogs: closeGlobalLogs,
  } = useStatusDialogs();

  const {
    register: globalRegister,
    handleSubmit: globalHandleSubmit,
    formState: { errors: globalErrors },
    setValue: globalSetValue,
    clearErrors: globalClearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(updateStatusSchema),
  });

  useEffect(() => {
    // Prefer live role id when available; fall back to constant while loading.
    if (superAdminRoleId) {
      setUserListQueryParam((prevParams) => ({
        ...prevParams,
        user_role_id: superAdminRoleId,
      }));
    }
  }, [superAdminRoleId]);


  // health org columns
  const healthOrgFields = [
    { id: "name", label: "Health Organisation Name", flex: 2, sortable: true },
    {
      id: "street_address",
      label: "Address",
      type: "address",
      flex: 2.5,
      render: (item: HealthOrganisation) => renderAddressCell(item.address),
    },
    {
      id: "phone",
      label: "Phone Number",
      type: "phone_number",
      render: (item: HealthOrganisation) => renderPhoneCell(item.address),
    },
    {
      id: "is_active",
      label: "Status",
      type: "badge",
      flex: 0.8,
      sortable: true,
      render: (item: HealthOrganisation) => renderStatusBadge(item.is_active),
    },
  ];

  // laboratory columns
  const laboratoryFields = [
    { id: "name", label: "Laboratory Name", flex: 2, sortable: true },
    {
      id: "street_address",
      label: "Address",
      type: "address",
      flex: 2.5,
      render: (item: Laboratory) => renderAddressCell(item.address),
    },
    {
      id: "phone",
      label: "Phone Number",
      type: "phone_number",
      render: (item: Laboratory) => renderPhoneCell(item.address),
    },
    {
      id: "is_active",
      label: "Status",
      type: "badge",
      flex: 0.8,
      sortable: true,
      render: (item: Laboratory) => renderStatusBadge(item.is_active),
    },
  ];

  const handleGlobalAdminStatusSubmit = useCallback(
    async (values: FormState) => {
      // Avoid sending a mutation without a selected target.
      if (!selectedGlobalAdmin?.id) {
        setGlobalMessage({
          type: "error",
          text: "No user selected for status update.",
        });
        return;
      }
      try {
        setGlobalLoading(true);
        await updateUser({
          id: selectedGlobalAdmin.id,
          is_active: !selectedGlobalAdmin.is_active,
          deactivate_reason: values.deactivate_reason ?? "",
        }).unwrap();
        setGlobalMessage({ type: "success", text: "Status updated successfully." });
        closeGlobalReason();
        globalSetValue("deactivate_reason", "");
        globalClearErrors();
        await refetchGlobalAdmins();
      } catch (error: unknown) {
        closeGlobalReason();
        setGlobalMessage({
          type: "error",
          text: getErrorMessage(error, "Something went wrong!"),
        });
      } finally {
        setGlobalLoading(false);
      }
    },
    [
      selectedGlobalAdmin,
      updateUser,
      closeGlobalReason,
      globalSetValue,
      globalClearErrors,
      refetchGlobalAdmins,
    ]
  );

  const handleGlobalAdminMenu = useCallback(
    (value: GlobalAdminAction, item: Users) => {
      setSelectedGlobalAdmin(item);
      if (value === "view") {
        navigate(`/users/details/${item?.id}`);
      } else if (value === "edit") {
        navigate(`/users/edit/${item?.id}`);
      } else if (value === "logs") {
        openGlobalLogs();
      } else if (value === "deactivate" || value === "activate") {
        openGlobalConfirm();
      }
    },
    [navigate, openGlobalLogs, openGlobalConfirm]
  );

  // global admin columns (include actions when tab is global)
  const globalFields = useMemo(
    () => [
      {
        id: "surname",
        label: "Name",
        flex: 2.5,
        sortable: true,
        type: "name",
        render: (item: Users) => renderGlobalNameCell(item),
      },
      {
        id: "mail",
        label: "Email Address",
        flex: 2.5,
        type: "name",
        render: (item: Users) =>
          renderCellText(item?.mail ?? "", { className: style.cell }),
      },
      {
        id: "is_active",
        label: "Status",
        type: "badge",
        flex: 1,
        sortable: true,
        render: (item: Users) => renderStatusBadge(item.is_active),
      },
      {
        id: "actions",
        label: "Actions",
        type: "action",
        render: (item: Users) => (
          <CustomActionMenu<Users, GlobalAdminAction>
            item={item}
            isActive={item?.is_active ?? false}
            handleChange={handleGlobalAdminMenu}
            actions={buildUserActions<GlobalAdminAction>([
              "view",
              ...(item.is_active ? (["edit"] as GlobalAdminAction[]) : []),
              "logs",
              item.is_active ? "deactivate" : "activate",
            ])}
          />
        ),
      },
    ],
    [handleGlobalAdminMenu]
  );

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: UsersTabKey
  ) => {
    setSelectedTab(newValue);
    // Reset search on tab switch to avoid mixing queries across datasets.
    if (newValue === "global") {
      setUserListQueryParam((prevParams) => ({
        ...prevParams,
        search_key: "",
      }));
    } else {
      setQueryParam((prevParams) => ({
        ...prevParams,
        search_key: "",
      }));
    }
  };

  // for searching in the query
  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (selectedTab === "global") {
        setUserListQueryParam((prevParams) => ({
          ...prevParams,
          search_key: value,
        }));
        return;
      }
      setQueryParam((prevParams) => ({
        ...prevParams,
        search_key: value,
      }));
    },
    [selectedTab]
  );

  const searchPlaceholder =
    selectedTab === "healthOrg"
      ? "health organisation"
      : selectedTab === "laboratory"
        ? "laboratory"
        : "global admin";

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: colors.lightGrey,
          alignItems: "center",
          padding: "10px 15px",
          marginTop: "15px",
        }}
        gap={2}
      >
        <Box width={"100%"} sx={{ backgroundColor: "primary.light" }}>
          <Tabs value={selectedTab} onChange={handleChange} variant="fullWidth">
            <Tab value="healthOrg" label="Health Organisation" />
            <Tab value="laboratory" label="Laboratory" />
            <Tab value="global" label="Global" />
          </Tabs>
        </Box>
        <MuiInput
          disableUnderline
          placeholder={`Search by ${searchPlaceholder}....`}
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={
            selectedTab === "global"
              ? userListQueryParam.search_key
              : queryParam.search_key
          }
          onChange={handleSearch}
          sx={{
            width: "400px",
            gap: "8px",
            marginBottom: 0,
            backgroundColor: colors.white,
            borderRadius: "50px",
          }}
        />
      </Box>
      <Box
        style={{
          maxHeight: "calc(100vh - 195px)",
          overflow: "auto",
          marginTop: "10px",
        }}
      >
        {selectedTab === "healthOrg" && (
          <CustomTable<HealthOrganisation>
            fields={healthOrgFields}
            data={healthOrgData?.items || []}
            loading={healthOrgFetching}
            onDoubleClick={(item: HealthOrganisation) => {
              navigate(`${item?.id}`, {
                state: {
                  label: item.name,
                  tab: 'Health Organisation',
                },
              });
            }}
            defaultOrderBy={"name"}
            searchTerm={queryParam.search_key}
            onClearSearch={() => setQueryParam((prev) => ({ ...prev, search_key: "" }))}
          />
        )}
        {selectedTab === "laboratory" && (
          <CustomTable<Laboratory>
            fields={laboratoryFields}
            data={laboratoryData?.items || []}
            loading={laboratoryFetching}
            onDoubleClick={(item: Laboratory) => {
              navigate(`${item?.id}`, {
                state: {
                  label: item.name,
                  tab: 'Laboratory',
                },
              });
            }}
            defaultOrderBy={"name"}
            searchTerm={queryParam.search_key}
            onClearSearch={() => setQueryParam((prev) => ({ ...prev, search_key: "" }))}
          />
        )}
        {selectedTab === "global" && (
          <>
            {globalMessage && (
              <Box mt={2} mb={2}>
                <LabelBox
                  boxProps={{ height: 50, display: "flex", alignItems: "center" }}
                  data={globalMessage}
                  onClose={() => setGlobalMessage(null)}
                />
              </Box>
            )}
            <CustomTable<Users>
              fields={globalFields}
              data={superAdminData?.items || []}
              loading={superAdminFetching}
              onDoubleClick={(item: Users) => {
                navigate(`/users/details/${item?.id}`);
              }}
              defaultOrderBy={"surname"}
              searchTerm={userListQueryParam.search_key}
              onClearSearch={() => setUserListQueryParam((prev) => ({ ...prev, search_key: "" }))}
            />
          </>
        )}
      </Box>
      {globalConfirmOpen && selectedGlobalAdmin && (
        <ConfirmationDialog
          visible={globalConfirmOpen}
          message={`Are you sure you want to ${selectedGlobalAdmin.is_active ? "deactivate " : "activate "
            }"${selectedGlobalAdmin.surname?.toUpperCase() ?? ""}, ${selectedGlobalAdmin.given_name ?? ""
            } ${selectedGlobalAdmin.prefix ?? ""}"?`}
          onClose={closeGlobalConfirm}
          onConfirm={() => {
            closeGlobalConfirm();
            if (selectedGlobalAdmin.is_active) {
              openGlobalReason();
            } else {
              handleGlobalAdminStatusSubmit({ deactivate_reason: "" });
            }
          }}
        />
      )}
      {globalReasonOpen && (
        <InputDialog
          visible={globalReasonOpen}
          onClose={() => {
            closeGlobalReason();
            globalSetValue("deactivate_reason", "");
            globalClearErrors();
          }}
        >
          <Box
            component="form"
            sx={{ marginX: "20px" }}
            onSubmit={globalHandleSubmit(handleGlobalAdminStatusSubmit)}
          >
            <Typography
              variant="h2"
              component="div"
              fontSize={16}
              fontWeight={700}
              color={colors.modalHeading}
              marginBottom={"10px"}
              sx={{ display: "flex", flexDirection: "row" }}
            >
              {"Enter the reason for deactivation"}
              <Typography
                color={"red"}
                fontSize={16}
                fontWeight={700}
                sx={{ marginLeft: "5px", marginTop: "-5px" }}
              >
                {"*"}
              </Typography>
            </Typography>
            <Input
              placeholder=""
              name="deactivate_reason"
              required
              register={globalRegister}
              multiline
              rows={3}
              errors={globalErrors}
              sx={{
                width: "100%",
                margin: "1px",
                borderWidth: "1px",
              }}
            />
            <Box className={style.horizontalButtons} sx={{ paddingX: "50px" }}>
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  backgroundColor: colors.white,
                  ":hover": { backgroundColor: colors.white },
                }}
                variant="outlined"
                onClick={() => {
                  closeGlobalReason();
                  globalSetValue("deactivate_reason", "");
                  globalClearErrors();
                }}
                label={"Cancel"}
              />
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label={"Submit"}
                loading={globalLoading}
              />
            </Box>
          </Box>
        </InputDialog>
      )}
      {globalLogsOpen && (
        <DetailsDialog
          visible={globalLogsOpen}
          details={selectedGlobalAdmin}
          onClose={closeGlobalLogs}
          onConfirm={closeGlobalLogs}
        />
      )}
    </Box>
  );
};

export default Users;
